// @flow
import * as React from 'react';

import { CircleX, ShieldCheck } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, Loader, Stepper, Text, View } from 'reshaped';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import { Alert, Badge, Button, Icon, Link } from '@gandi/reshaped';
import { useTranslator, mapTags } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout/components/Intro';
import { useGetPrice } from '../helpers';

import { useTmchPrices } from './hooks/useTmchPrices';

const Overview = (): React.Node => {
  const { prices, pricesFailure, pricesFetching, pricesLoaded } =
    useTmchPrices();
  const getPrice = useGetPrice();
  const translator = useTranslator();
  const { __, formatPrice } = translator;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const refer = Array.isArray(prices) && prices.find(e => e.min_duration === 1);

  return (
    <>
      <Intro title={__('TMCH Trademark Registration')} />

      {pricesFailure && !pricesFetching && (
        <View paddingBottom={10}>
          <Alert title={__('Unexpected error')} color="critical" icon={CircleX}>
            {__('Unexpected error occurred. Please try again later.')}
          </Alert>
        </View>
      )}

      {!pricesLoaded && !pricesFailure && prices.length === 0 && (
        <View align="center">
          <Loader size="large" ariaLabel={__('Loading...')} />
        </View>
      )}

      {pricesLoaded && (
        <>
          <Card padding={6}>
            <View gap={2} direction="row" paddingBottom={5}>
              <Icon
                svg={ShieldCheck}
                size={7}
                attributes={{ style: { color: 'var(--rs-color-brand)' } }}
              />
              <View.Item grow>
                <Text as="h3" variant="featured-3" weight="bold" wrap="balance">
                  {__(
                    'Ask Gandi to register your trademark in the Trademark ClearingHouse database',
                  )}
                </Text>
              </View.Item>
            </View>

            {/* TODO: Go back to `<View as="p" paddingBottom={5}>`
            when we update to Reshaped 4
            Cf github.com/reshaped-ui/reshaped/issues/620 */}

            <p style={{ paddingBottom: 'var(--rs-unit-x5)' }}>
              {mapTags(
                __(
                  'Simplify your Sunrise domain registration workflow by managing your SMD file at Gandi. We will handle ' +
                    'the complex part, so you can focus on your brand strategy. [[link:Learn more]].',
                ),
                ({ link }) => ({
                  link: (
                    <Link
                      href={__(
                        'https://docs.gandi.net/en/domain_names/domain_services/tmch.html',
                      )}
                      attributes={{
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      }}
                    >
                      {link}
                    </Link>
                  ),
                }),
              )}
            </p>

            <View paddingBottom={10}>
              <Stepper direction="column">
                <Stepper.Item
                  title={mapTags(
                    __(
                      'Download and fill the [[link:Declaration of proof of use template]]',
                    ),
                    ({ link }) => ({
                      link: (
                        <Link
                          // XXX: Put the url in conf?
                          href="https://www.trademark-clearinghouse.com/sites/default/files/files/downloads/declaration_of_proof_of_use_-_tm_holder.pdf"
                          attributes={{
                            target: '_blank',
                            rel: 'noopener noreferrer',
                            download: true,
                          }}
                        >
                          {link}
                        </Link>
                      ),
                    }),
                  )}
                />
                <Stepper.Item
                  title={__(
                    'Complete the form and provide the required documents',
                  )}
                />
                <Stepper.Item
                  title={__(
                    'Submit & pay. You will then receive an email to confirm your request.',
                  )}
                />
              </Stepper>
            </View>

            <Button
              color="primary"
              fullWidth={{
                s: true,
                m: false,
              }}
              render={attributes => (
                // $FlowFixMe[incompatible-type] Reshaped (flow) render props (mixed) are not compatible with react router Link props
                <RouterLink {...attributes} to="/tmch/tmch-registration" />
              )}
            >
              {__('File a request')}
            </Button>
          </Card>

          <View paddingTop={14} paddingBottom={8}>
            <Text as="h2" variant="featured-3" weight="bold" align="center">
              {__('How much does it cost?')}
            </Text>
          </View>

          <Card padding={6}>
            <View as="p" paddingBottom={5}>
              {__(
                'Prices vary depending on the trademark registration duration',
              )}
            </View>

            {Array.isArray(prices) && prices.length > 0 && (
              <View as="ul" gap={4}>
                {prices.map(price => (
                  <View
                    as="li"
                    gap={3}
                    direction="row"
                    key={price.min_duration}
                  >
                    <Badge color="primary">
                      {__('%(count)s years', { count: price.max_duration })}
                    </Badge>
                    {price.min_duration !== 1 && refer && (
                      <del>
                        {formatPrice(
                          getPrice(refer) * price.min_duration,
                          price.currency,
                        )}
                      </del>
                    )}

                    <span>
                      {formatPrice(
                        getPrice(price) * price.min_duration,
                        price.currency,
                      )}
                    </span>
                    {price.taxes.length > 0 && (
                      <Text as="span" color="neutral-faded">
                        {displayTaxes ? __('incl. taxes') : __('excl. taxes')}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}
          </Card>
        </>
      )}
    </>
  );
};

export default Overview;
